// MIT License - Copyright (c) 2026 Doug McNall

/**
 * Achievement Manager
 * 
 * Tracks player progress and unlocks achievements.
 * Persists to localStorage and syncs with API.
 */

import ACHIEVEMENTS from '../../config/AchievementsConfig';
import EventBus, { EVENT_TYPES } from './EventBus';

const STORAGE_KEY = 'starship_achievements';
const STATS_KEY = 'starship_achievement_stats';

class AchievementManager {
  constructor() {
    this.unlockedAchievements = new Set();
    this.stats = {};
    this.load();
  }

  /**
   * Load achievements from localStorage
   */
  load() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        this.unlockedAchievements = new Set(JSON.parse(saved));
      }

      const savedStats = localStorage.getItem(STATS_KEY);
      if (savedStats) {
        const parsed = JSON.parse(savedStats);
        this.stats = parsed;
        
        // Convert arrays back to Sets (or initialize if missing)
        this.stats.visited_systems = new Set(parsed.visited_systems || []);
        this.stats.encountered_aliens = new Set(parsed.encountered_aliens || []);
        this.stats.visited_universes = new Set(parsed.visited_universes || ['milkyway']);
      } else {
        this.initializeStats();
      }
    } catch (error) {
      console.error('[Achievements] Load failed:', error);
      this.initializeStats();
    }
  }

  /**
   * Initialize stats tracking
   */
  initializeStats() {
    this.stats = {
      systems_visited: 0,
      scans_performed: 0,
      light_years_traveled: 0,
      aliens_encountered: 0,
      combat_victories: 0,
      enemies_destroyed: 0,
      ace_status_achieved: 0,
      survived_low_hull: 0,
      perfect_victories: 0,
      max_combo: 0,
      trades_completed: 0,
      max_credits: 0,
      max_trade_profit: 0,
      upgrades_purchased: 0,
      all_upgrades_purchased: 0,
      modules_installed: 0,
      all_weapon_slots_unlocked: 0,
      universes_visited: 1, // Start at 1 (Milky Way)
      black_holes_discovered: 0,
      max_warp_factor: 1,
      efficient_travel: 0,
      play_time_hours: 0,
      exact_credits: 0,
      stranded: 0,
      hull_at_1_percent: 0,
      all_achievements: 0,
      
      // Tracking sets
      visited_systems: new Set(),
      encountered_aliens: new Set(),
      visited_universes: new Set(['milkyway'])
    };
  }

  /**
   * Save achievements to localStorage
   */
  save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...this.unlockedAchievements]));
      
      // Convert sets to arrays for storage
      const statsToSave = {
        ...this.stats,
        visited_systems: [...this.stats.visited_systems],
        encountered_aliens: [...this.stats.encountered_aliens],
        visited_universes: [...this.stats.visited_universes]
      };
      localStorage.setItem(STATS_KEY, JSON.stringify(statsToSave));
    } catch (error) {
      console.error('[Achievements] Save failed:', error);
    }
  }

  /**
   * Check if achievement is unlocked
   */
  isUnlocked(achievementId) {
    return this.unlockedAchievements.has(achievementId);
  }

  /**
   * Unlock an achievement
   */
  unlock(achievementId) {
    if (this.isUnlocked(achievementId)) {
      return false; // Already unlocked
    }

    const achievement = ACHIEVEMENTS[achievementId];
    if (!achievement) {
      console.warn('[Achievements] Unknown achievement:', achievementId);
      return false;
    }

    this.unlockedAchievements.add(achievementId);
    this.save();

    // Emit event for UI notification
    EventBus.emit(EVENT_TYPES.ACHIEVEMENT_UNLOCKED, {
      achievement,
      timestamp: Date.now()
    });

    return true;
  }

  /**
   * Check all achievements for unlocks
   */
  checkAchievements() {
    Object.values(ACHIEVEMENTS).forEach(achievement => {
      if (this.isUnlocked(achievement.id)) return;
      
      if (this.meetsRequirement(achievement.requirement)) {
        this.unlock(achievement.id);
      }
    });
  }

  /**
   * Check if requirement is met
   */
  meetsRequirement(requirement) {
    const { type, value } = requirement;
    const stat = this.stats[type];

    if (stat === undefined) return false;
    
    // Handle set-based stats
    if (stat instanceof Set) {
      return stat.size >= value;
    }
    
    return stat >= value;
  }

  /**
   * Update a stat and check achievements
   */
  updateStat(statName, value) {
    if (this.stats[statName] === undefined) {
      console.warn('[Achievements] Unknown stat:', statName);
      return;
    }

    this.stats[statName] = value;
    this.save();
    this.checkAchievements();
  }

  /**
   * Increment a stat and check achievements
   */
  incrementStat(statName, amount = 1) {
    if (this.stats[statName] === undefined) {
      console.warn('[Achievements] Unknown stat:', statName);
      return;
    }

    this.stats[statName] += amount;
    this.save();
    this.checkAchievements();
  }

  /**
   * Track system visit
   */
  visitSystem(systemId) {
    if (!this.stats.visited_systems.has(systemId)) {
      this.stats.visited_systems.add(systemId);
      this.stats.systems_visited = this.stats.visited_systems.size;
      this.save();
      this.checkAchievements();
    }
  }

  /**
   * Track alien encounter
   */
  encounterAlien(alienId) {
    if (!this.stats.encountered_aliens.has(alienId)) {
      this.stats.encountered_aliens.add(alienId);
      this.stats.aliens_encountered = this.stats.encountered_aliens.size;
      this.save();
      this.checkAchievements();
    }
  }

  /**
   * Track universe visit
   */
  visitUniverse(universeId) {
    if (!this.stats.visited_universes.has(universeId)) {
      this.stats.visited_universes.add(universeId);
      this.stats.universes_visited = this.stats.visited_universes.size;
      this.save();
      this.checkAchievements();
    }
  }

  /**
   * Track combat victory
   */
  recordCombatVictory(data = {}) {
    this.incrementStat('combat_victories');
    
    if (data.enemiesDestroyed) {
      this.incrementStat('enemies_destroyed', data.enemiesDestroyed);
    }
    
    if (data.perfectVictory) {
      this.incrementStat('perfect_victories');
    }
    
    if (data.lowHullSurvival) {
      this.incrementStat('survived_low_hull');
    }
    
    if (data.maxCombo && data.maxCombo > this.stats.max_combo) {
      this.updateStat('max_combo', data.maxCombo);
    }
    
    if (data.aceStatus) {
      this.incrementStat('ace_status_achieved');
    }
  }

  /**
   * Track trade
   */
  recordTrade(profit) {
    this.incrementStat('trades_completed');
    
    if (profit > this.stats.max_trade_profit) {
      this.updateStat('max_trade_profit', profit);
    }
  }

  /**
   * Track credits
   */
  updateCredits(credits) {
    this.updateStat('exact_credits', credits);
    
    if (credits > this.stats.max_credits) {
      this.updateStat('max_credits', credits);
    }
  }

  /**
   * Get achievement progress
   */
  getProgress() {
    const total = Object.keys(ACHIEVEMENTS).length;
    const unlocked = this.unlockedAchievements.size;
    const percentage = Math.round((unlocked / total) * 100);
    
    const totalPoints = Object.values(ACHIEVEMENTS).reduce((sum, a) => sum + a.points, 0);
    const earnedPoints = [...this.unlockedAchievements].reduce((sum, id) => {
      return sum + (ACHIEVEMENTS[id]?.points || 0);
    }, 0);
    
    return {
      total,
      unlocked,
      percentage,
      totalPoints,
      earnedPoints
    };
  }

  /**
   * Get unlocked achievements
   */
  getUnlockedAchievements() {
    return [...this.unlockedAchievements].map(id => ACHIEVEMENTS[id]).filter(Boolean);
  }

  /**
   * Get locked achievements
   */
  getLockedAchievements() {
    return Object.values(ACHIEVEMENTS).filter(a => !this.isUnlocked(a.id));
  }

  /**
   * Get achievement progress for a specific achievement
   */
  getAchievementProgress(achievementId) {
    const achievement = ACHIEVEMENTS[achievementId];
    if (!achievement) return null;

    const { type, value } = achievement.requirement;
    const current = this.stats[type];
    
    if (current instanceof Set) {
      return {
        current: current.size,
        required: value,
        percentage: Math.min(100, Math.round((current.size / value) * 100))
      };
    }
    
    return {
      current,
      required: value,
      percentage: Math.min(100, Math.round((current / value) * 100))
    };
  }

  /**
   * Reset all achievements (for testing)
   */
  reset() {
    this.unlockedAchievements.clear();
    this.initializeStats();
    this.save();
  }
}

const achievementManager = new AchievementManager();
export default achievementManager;
